export const AI_CHAT_MD_TABLE_WRAP_CLASS = "playbook-chat-md-table-wrap";

const SEPARATOR_CELL = /^\s*:?-{3,}:?\s*$/;

const ALIGN_CLASS = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
};

const normalizeAlign = (align) => (ALIGN_CLASS[align] ? align : "left");

export const splitGfmTableRow = (line) => {
  const trimmed = String(line || "").trim();
  if (!trimmed.includes("|")) return null;

  let body = trimmed.replace(/\\\|/g, "\u0000");
  if (body.startsWith("|")) body = body.slice(1);
  if (body.endsWith("|")) body = body.slice(0, -1);

  const cells = body
    .split("|")
    .map((cell) => cell.trim().replace(/\u0000/g, "|"));
  if (cells.length < 2) return null;
  return cells;
};

export const isGfmSeparatorRow = (cells) =>
  Boolean(cells) &&
  cells.length >= 2 &&
  cells.every(
    (cell) => SEPARATOR_CELL.test(cell) && String(cell).includes("-")
  );

export const gfmAlignments = (separatorCells) =>
  (separatorCells || []).map((cell) => {
    const value = String(cell || "").trim();
    const left = value.startsWith(":");
    const right = value.endsWith(":");
    if (left && right) return "center";
    if (right) return "right";
    return "left";
  });

const padRow = (row, colCount) => {
  const next = (row || []).slice(0, colCount);
  while (next.length < colCount) next.push("");
  return next;
};

export const parseGfmTable = (lines, start) => {
  const header = splitGfmTableRow(lines[start]);
  if (!header) return null;

  const separator = splitGfmTableRow(lines[start + 1] || "");
  if (!isGfmSeparatorRow(separator)) return null;

  const colCount = Math.max(header.length, separator.length);
  const alignments = padRow(gfmAlignments(separator), colCount).map(
    normalizeAlign
  );
  const rows = [];
  let index = start + 2;

  while (index < lines.length) {
    const line = lines[index];
    if (!String(line || "").trim()) break;
    const row = splitGfmTableRow(line);
    if (!row || isGfmSeparatorRow(row)) break;
    if (isGfmSeparatorRow(splitGfmTableRow(lines[index + 1] || ""))) break;
    rows.push(padRow(row, colCount));
    index += 1;
  }

  return {
    headers: padRow(header, colCount),
    alignments,
    rows,
    end: index,
  };
};

export const markdownContentBlocks = (text) => {
  const lines = String(text ?? "").split("\n");
  const blocks = [];
  let textLines = [];

  const flushText = () => {
    if (textLines.length === 0) return;
    blocks.push({ type: "text", text: textLines.join("\n") });
    textLines = [];
  };

  let index = 0;
  let inFence = false;
  while (index < lines.length) {
    if (/^\s*(```|~~~)/.test(lines[index])) inFence = !inFence;
    const parsed = inFence ? null : parseGfmTable(lines, index);
    if (parsed) {
      flushText();
      blocks.push({
        type: "table",
        headers: parsed.headers,
        alignments: parsed.alignments,
        rows: parsed.rows,
      });
      index = parsed.end;
    } else {
      textLines.push(lines[index]);
      index += 1;
    }
  }

  flushText();
  return blocks;
};

const cellHtml = (tag, value, align, extraClass = "") => {
  const extras = extraClass ? ` ${extraClass}` : "";
  const resolved = normalizeAlign(align);
  return `<${tag} class="border border-gray-300 dark:border-slate-600 px-2 py-1.5 ${ALIGN_CLASS[resolved]}${extras}" data-align="${resolved}">${value}</${tag}>`;
};

export const renderGfmTableHtml = ({ headers, alignments, rows }) => {
  const cols = headers || [];
  const aligns = alignments || [];
  const headerCells = cols
    .map((cell, index) =>
      cellHtml(
        "th",
        cell,
        aligns[index] || "left",
        "bg-gray-50 dark:bg-slate-700 font-semibold"
      )
    )
    .join("");
  const body = (rows || [])
    .map((row) => {
      const cells = row
        .map((cell, index) => cellHtml("td", cell, aligns[index] || "left"))
        .join("");
      return `<tr>${cells}</tr>`;
    })
    .join("");

  return (
    `<div class="${AI_CHAT_MD_TABLE_WRAP_CLASS} overflow-x-auto my-2 max-w-full">` +
    `<table class="playbook-chat-md-table w-full border-collapse text-left">` +
    `<thead><tr>${headerCells}</tr></thead>` +
    `<tbody>${body}</tbody>` +
    `</table></div>`
  );
};

export const formatMarkdownTables = (text) =>
  markdownContentBlocks(text)
    .map((block) =>
      block.type === "table" ? renderGfmTableHtml(block) : block.text
    )
    .join("\n");
